import React from 'react'
import { setCacheHeaders } from '@scentregroup/shared/helpers/set-cache-headers'
import { MOVIE_DAYS_IN_ADVANCE } from '@scentregroup/shared/constants'
import { GetMoviesQuery, TradingHourDay } from '@scentregroup/shared/graphql'
import {
  CentreStaticData,
  fetchCentreStaticData,
} from '@scentregroup/shared/hub-components/universal-header'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import Layout from '@scentregroup/shared/hub-components/layout'
import { useGetHrefContext } from '@scentregroup/shared/context'
import {
  DateTime,
  getWeekAhead,
} from '@scentregroup/shared/helpers/date-helpers'
import NowShowing from '@scentregroup/shared/components/now-showing'
import getMoviesWithSessions from '@scentregroup/shared/helpers/get-movies-with-sessions'
import {
  getCentreDetails,
  CentreDetails,
  buildContactLinks,
} from '@scentregroup/shared/helpers/centre'
import { useStoredCentre } from '@scentregroup/shared/hooks/use-stored-centre'
import { slugify } from '@scentregroup/shared/helpers'
import { useRouter } from 'next/router'
import { completelyCanonicalUrl } from '@scentregroup/shared/helpers/canonical-link'
import { ItemList, ListItem, WithContext } from 'schema-dts'
import { SGLinkedData } from '@scentregroup/shared/hub-components/json-ld'
import {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
} from '../../../types/next'
import { SGPageTemplateWithAdZones } from '../../../../../src/types/page-templates'
import { makeApolloClient } from '../../../lib/apolloClient'
import { getCentrePageTemplate } from '../../../lib/page-templates/fetching'
import {
  SimpleStore,
  getCentreCinema,
} from '../../../lib/get-content-detail/cinema'
import { StoreContentDetail } from '../../../components/page-templates/content-detail/store'
import {
  CentreSlug,
  StoreId,
  DateString,
  getTradingHours,
} from '../../../lib/get-trading-hours'
import { getMovies } from '../../../lib/get-content-detail/movies'
import {
  getTopStripProps,
  ITopStripProps,
} from '@scentregroup/shared/hub-components/top-strip'
import { shouldRedirectForMismatchedRoute } from '@scentregroup/shared/lib/redirects'

interface ICentreCinemaPageProps {
  topStripProps: ITopStripProps | null
  headerStaticData: CentreStaticData
  centre: CentreDetails
  template: SGPageTemplateWithAdZones
  tradingHours: TradingHourDay[]
  country: 'Australia' | 'New Zealand'
  cinema: SimpleStore
  movies: NonNullable<GetMoviesQuery['movies']>
}

export async function getServerSideProps(
  ctx: GetServerSidePropsContext<{
    centre: string
    id: string
    slug: string
  }>
): Promise<GetServerSidePropsResult<ICentreCinemaPageProps>> {
  const centreSlug = ctx.params?.centre
  if (!centreSlug) {
    return {
      notFound: true,
    }
  }
  setCacheHeaders(ctx.res)
  const client = makeApolloClient()
  const country = localeToCountry(ctx.locale)
  const centre = await getCentreDetails(client, { slug: centreSlug, country })
  if (!centre) {
    return {
      notFound: true,
    }
  }
  const redirectForCentreSlug = shouldRedirectForMismatchedRoute(
    '/[centre]/movies',
    ctx.params,
    {
      centre: centre.slug,
    }
  )
  if (redirectForCentreSlug) {
    return redirectForCentreSlug
  }

  const [template, headerStaticData, topStripProps] = await Promise.all([
    getCentrePageTemplate(client, centreSlug, 'movie'),
    fetchCentreStaticData(client, {
      country: localeToCountry(ctx.locale),
      centre,
    }),
    getTopStripProps(client, country, centreSlug),
  ])

  if (!template) {
    return {
      notFound: true,
    }
  }

  const [cinema, movies] = await Promise.all([
    getCentreCinema(client, centre?.sys.id),
    getMovies(client, centreSlug),
  ])
  if (!cinema || !movies) {
    return {
      notFound: true,
    }
  }

  const startDate = DateTime.moment()
    .tz(centre.timeZone)
    .startOf('day')
    .format('YYYY-MM-DD')
    .toString() as DateString
  const tradingHours = await getTradingHours(
    client,
    centreSlug as CentreSlug,
    cinema?.sys?.id as StoreId,
    startDate
  )

  return {
    props: {
      country,
      topStripProps,
      centre,
      cinema,
      template,
      headerStaticData,
      tradingHours,
      movies,
    },
  }
}

const MoviesPage: React.FC<
  React.PropsWithChildren<ICentreCinemaPageProps>
> = props => {
  const {
    country,
    topStripProps,
    centre,
    cinema,
    template,
    headerStaticData,
    tradingHours,
    movies,
  } = props
  const canonical = completelyCanonicalUrl(useGetHrefContext()())
  const metaData = {
    title: `Movie Times at Westfield ${centre.title}`,
    description: `Westfield ${centre.title} - home to your favourite Movie selection. Explore our range of Movie options, events & offers at Westfield ${centre.title}.`,
    canonical,
  }
  const contactLinks = buildContactLinks(centre)
  useStoredCentre(centre)
  const router = useRouter()

  const schema = React.useMemo<WithContext<ItemList>>(() => {
    const domain = router.domainLocales?.filter(
      ({ defaultLocale }) => defaultLocale === router.locale
    )[0].domain

    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      '@id': `${completelyCanonicalUrl(
        `${domain}/${centre.slug}/movies`
      )}#itemlist`,
      itemListElement: movies.map<ListItem>((movie, position) => ({
        '@type': 'ListItem',
        position: String(position + 1),
        item: {
          '@type': 'Movie',
          url: completelyCanonicalUrl(
            `${domain}/${centre.slug}/movie/${movie.id}/${slugify(movie.title)}`
          ),
          name: movie.title,
          ...(movie.imageUrl ? { image: movie.imageUrl } : {}),
        },
      })),
    }
  }, [centre.slug, movies, router.domainLocales, router.locale])

  return (
    <>
      <SGLinkedData item={schema} />
      <Layout
        country={country}
        topStrip={topStripProps}
        centre={{
          ...centre,
          contactLinks,
        }}
        header={{
          variant: 'centre',
          data: headerStaticData,
          logoText: centre.title,
        }}
        metaData={metaData}
      >
        <StoreContentDetail
          centre={centre}
          content={cinema}
          template={template}
          storeTradingHours={tradingHours}
          centreTradingHours={tradingHours}
        >
          <NowShowing
            title="Now showing"
            entries={getMoviesWithSessions(movies, centre.timeZone)}
            centreSlug={centre.slug}
            tabHeadings={getWeekAhead(centre.timeZone, MOVIE_DAYS_IN_ADVANCE)}
          />
        </StoreContentDetail>
      </Layout>
    </>
  )
}
export default MoviesPage
